// Client sign-in page for the SeaState console.
//
// A single centered card. On submit it calls the typed API client; a 401 shows
// "Wrong email or password.", anything else shows "Server unavailable." There
// is no self-service password reset — a muted line points to the engagement
// lead. No framework: we build the DOM once and mutate the pieces we own.
//
// Two of the server's answers are NOT about the password and must not be shown
// as though they were (server/ratelimit.go): a 429 when this address or account
// has failed too often lately, and a 503 when every argon2 slot was held. Both
// carry Retry-After, and both are stated as a wait — an operator who cannot
// tell a throttle from a wrong password will keep retyping a password that was
// never wrong, which is the failure mode the wait exists to prevent.

import { isServerBusy, login } from "../data/api.js";
import { RETRY_NOW, SERVER_BUSY, startCountdown, throttledSignIn } from "../ui/retry.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A bottom-border field (label.fld / .lab) wrapping a typed input. */
function field(labelText: string, type: string, autocomplete: AutoFill): {
  wrap: HTMLLabelElement;
  input: HTMLInputElement;
} {
  const wrap = el("label", "fld");
  wrap.appendChild(el("span", "lab", labelText));
  const input = el("input");
  input.type = type;
  input.required = true;
  input.autocomplete = autocomplete;
  wrap.appendChild(input);
  return { wrap, input };
}

export function mountLogin(container: HTMLElement, onSuccess: () => void): void {
  const wrap = el("div", "login-wrap");
  const card = el("form", "block login-card");

  card.appendChild(el("div", "eyebrow", "Client access"));
  card.appendChild(el("h2", undefined, "Sign in"));
  card.appendChild(
    el("p", "note", "Your fleet's operating limits and vessel settings."),
  );

  const email = field("Email", "email", "username");
  const password = field("Password", "password", "current-password");
  card.append(email.wrap, password.wrap);

  const error = el("p", "note field-err");
  error.hidden = true;
  card.appendChild(error);

  const submit = el("button", "btn primary");
  submit.type = "submit";
  submit.textContent = "Sign in";
  submit.style.width = "100%";
  card.appendChild(submit);

  card.appendChild(
    el("p", "note", "Trouble signing in? Contact your engagement lead."),
  );

  let busy = false; // a sign-in is in flight
  let held = false; // the server named a wait and it has not run out yet
  let stopCountdown: (() => void) | null = null;

  /** `hold` marks the message as a wait rather than a refusal: amber, not the
   *  red this line uses when a password is wrong. */
  const showError = (message: string, hold = false): void => {
    error.textContent = message;
    error.classList.toggle("hold", hold);
    error.hidden = false;
  };

  /**
   * The server refused this attempt for time rather than for the password.
   * State the wait, count it down, and keep the button down until it runs out —
   * a retry before then is refused again without ever reaching a hash, so
   * letting it through would only replace the countdown with a fresh one.
   */
  const holdFor = (seconds: number | null): void => {
    stopCountdown?.();
    stopCountdown = null;
    showError(throttledSignIn(seconds), true);
    if (seconds === null) return; // no readable wait: say so, leave the button live
    held = true;
    submit.disabled = true;
    stopCountdown = startCountdown(
      seconds,
      (left) => showError(throttledSignIn(left), true),
      () => {
        stopCountdown = null;
        held = false;
        submit.disabled = false;
        showError(RETRY_NOW, true);
      },
    );
  };

  card.addEventListener("submit", (event: SubmitEvent) => {
    event.preventDefault();
    if (busy || held) return;
    error.hidden = true;

    busy = true;
    submit.disabled = true;
    submit.textContent = "Signing in…";

    void login(email.input.value.trim(), password.input.value).then((res) => {
      if (res.ok) {
        stopCountdown?.();
        onSuccess();
        return;
      }
      busy = false;
      submit.disabled = false;
      submit.textContent = "Sign in";
      if (res.status === 429) {
        // Throttled: the password was never looked at, so the operator is
        // owed the wait rather than a verdict on their credentials.
        holdFor(res.retryAfter ?? null);
        return;
      }
      if (isServerBusy(res)) {
        // Every hashing slot was held, so nothing was checked. Capacity, not
        // credentials — and Retry-After is 1 s, so there is nothing to count.
        showError(SERVER_BUSY, true);
        return;
      }
      showError(
        res.status === 401 ? "Wrong email or password." : "Server unavailable.",
      );
    });
  });

  wrap.appendChild(card);
  container.replaceChildren(wrap);
  email.input.focus();
}
